import Search from "./layout/Search.tsx";
import SearchTitleResults from "./titles/SearchTitleResults.tsx";
import { searchTitlesLicenceLoader } from "./titles/searchTitlesLicense.ts";
import InsetHomeError from "../InsetHomeError.tsx";
import StartScreen from "./start-screen/StartScreen.tsx";
import SearchCountsResults from "./book-content/SearchCountsResults.tsx";
import LibrarySearch from "./library/LibrarySearch.tsx";
import {
  searchCounts,
  searchCountsWithNextCursor,
} from "./book-content/searchCounts.ts";
import { LoaderFunction, redirect, matchPath } from "react-router-dom";
import { getSearchQuery } from "./query.ts";
import { searchContent } from "./book-content/searchContent.ts";
import { licenseLoader } from "../actions/license/licenseLoader.ts";
import { timeoutLoader } from "./common/timeoutLoader.ts";
import { favoritesByVbid } from "../actions/favorites/favorites.ts";
import { searchTitlesExploreLoader } from "./titles/searchTitlesExplore.ts";
import { searchTitlesStoreLoader } from "./titles/searchTitlesStore.ts";
import { coursePickerLoader } from "../common/course-picker/coursePickerLoader.ts";
import { getBasePathWithTrailingSlash } from "../../../common/env.ts";
import StartSampleScreen from "./start-screen/SearchSampleScreen.tsx";
import SampleResults from "./sample/titles/SampleResults.tsx";
import { sampleTitleResultsLoader } from "./sample/results/sampleTitleResultsLoader.ts";

// Default search mode redirector - redirects to sample by default
const searchModeRedirector: LoaderFunction = ({ request }) => {
  const url = new URL(request.url);
  const searchQuery = url.searchParams.get("q");

  // If there's a search query, redirect to library search with the query
  // This handles backwards compatibility for URLs like /search?q=math
  if (searchQuery) {
    return redirect(
      `/search/library/titles?q=${encodeURIComponent(searchQuery)}`
    );
  }

  // Redirect to sample by default - the Search component will redirect to library if user is not eligible
  return redirect("/search/sample");
};

// Shared query redirector logic
const createQueryRedirector = (mode: "library" | "sample"): LoaderFunction => {
  return ({ request }) => {
    const url = new URL(request.url);
    const basePath = getBasePathWithTrailingSlash();
    const isRootURL = !!matchPath(basePath + `search/${mode}`, url.pathname);
    const hasQuery = !!getSearchQuery(url.searchParams);

    // Redirect /search/{mode}/titles with no ?q= back to /search/{mode}
    if (!isRootURL && !hasQuery) {
      return redirect(`/search/${mode}`);
    }

    // Redirect /search/{mode}?q= to /search/{mode}/titles?q=
    if (isRootURL && hasQuery) {
      return redirect(`titles${url.search}`);
    }

    return null;
  };
};

// Library query redirector
const libraryQueryRedirector = createQueryRedirector("library");

// Sample query redirector
const sampleQueryRedirector = createQueryRedirector("sample");

export const searchRoutes = [
  {
    path: "/search/terms/:term/departments/:department/courses/:course/sections/:section",
    async lazy() {
      const { Component, loader } = await import(
        "./course-browse/lazyRoute.ts"
      );
      return { Component, loader };
    },
  },
  {
    path: "/search",
    element: <Search />,
    children: [
      // Root index - redirect to sample by default
      {
        index: true,
        loader: searchModeRedirector,
      },
      // Library search routes
      {
        path: "/search/library",
        element: <LibrarySearch />,
        loader: libraryQueryRedirector,
        children: [
          {
            index: true,
            element: <StartScreen />,
          },
          {
            path: "/search/library/titles",
            element: <SearchTitleResults />,
            errorElement: <InsetHomeError />,
          },
          {
            path: "/search/library/book-content",
            element: <SearchCountsResults />,
            errorElement: <InsetHomeError />,
          },
        ],
      },
      // Sample search routes
      {
        path: "/search/sample",
        loader: sampleQueryRedirector,
        children: [
          {
            index: true,
            element: <StartSampleScreen />,
          },
          {
            path: "/search/sample/titles",
            element: <SampleResults />,
          },
        ],
      },
      // Backward compatibility redirects - old paths to new library paths
      {
        path: "/search/titles",
        loader: ({ request }: { request: Request }) => {
          const url = new URL(request.url);
          return redirect(`/search/library/titles${url.search}`);
        },
      },
      {
        path: "/search/book-content",
        loader: ({ request }: { request: Request }) => {
          const url = new URL(request.url);
          return redirect(`/search/library/book-content${url.search}`);
        },
      },
      {
        path: "/search/api",
        children: [
          // Rapidfire activate/return actions cause fetcher and revalidation
          // cancellations, so the UI is out of sync with updates until the final
          // revalidation completes. To avoid this, prevent revalidation and
          // manage all activation/return updates in local state only.
          // See ListTitleResultCard.tsx

          // Revalidation after returning/activating assets could also lead to
          // position shifts since activated assets are part of the user's license.
          // Seeing an asset automatically move from the Explore section
          // to the license section could be a weird user experience.
          {
            path: "/search/api/titles/license",
            loader: searchTitlesLicenceLoader,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/titles/explore",
            loader: searchTitlesExploreLoader,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/titles/store",
            loader: searchTitlesStoreLoader,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/counts",
            loader: searchCounts,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/paginated-counts/:cursor",
            loader: searchCountsWithNextCursor,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/content/:vbid",
            loader: searchContent,
            // the only thing that should cause a content search revalidation is the search
            // itself, don't reload when borrow status changes etc
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/typeahead",
            loader: licenseLoader,
            shouldRevalidate: () => false,
          },
          // TO-DO: remove this timeout endpoint (added to ensure borrow/return fetcher
          // loading status despite the revalidation prevention via the license endpoint)
          // once we have a better way to show the asset changes after activation/return.
          {
            path: "/search/api/timeout",
            loader: timeoutLoader,
            shouldRevalidate: () => true,
          },
          {
            path: "/search/api/favorites-by-vbid",
            loader: favoritesByVbid,
            shouldRevalidate: () => false,
          },
          {
            path: "/search/api/courses/campus/:campus?/:term?/:department?/:course?",
            loader: coursePickerLoader,
          },
          {
            path: "/search/api/courses/:term?/:department?/:course?",
            loader: coursePickerLoader,
          },
          {
            path: "/search/api/sample",
            loader: sampleTitleResultsLoader,
            shouldRevalidate: () => false,
          },
        ],
      },
    ],
    errorElement: <InsetHomeError />,
  },
];
